import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { getVIPName } from 'src/app/shared/models/constants/vip-levels';
import { Subscription } from 'rxjs';
import { vipCheckboxesValidator } from 'src/app/shared/validators/registration-validators';

@Component({
  selector: 'app-vip-checkbox-group',
  templateUrl: './vip-checkbox-group.component.html',
  styleUrls: ['./vip-checkbox-group.component.scss'],
})

/**
 * Component for managing a group of VIP checkboxes.
 * This component provides functionality for handling multiple checkbox selections
 * related to VIP status or permissions.
 */
export class VipCheckboxGroupComponent implements OnInit, OnDestroy {
  @Input() control!: FormControl<number[] | null>;
  @Input() printerSeries?: string[];
  @Input() availablePrinterSeries?: any[];
  @Input() availableBusinessLevels?: any;
  @Input() isEnabled = false;

  familiesForms: any = {};

  getVIPName = getVIPName;

  private subscriptions: Subscription = new Subscription();

  /**
   * Creates an instance of VipCheckboxGroupComponent
   */
  constructor(private fb: FormBuilder) {}

  /**
   * Lifecycle hook that is called after data-bound properties are initialized
   */
  ngOnInit(): void {
    this.initializeFormControls();
    if (this.isEnabled) {
      this.control.setValidators(vipCheckboxesValidator(1));
      this.control.updateValueAndValidity();
    }
  }

  /**
   * Lifecycle hook that is called when the component is destroyed
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Handles the toggle change event
   * @param {boolean} checked The new toggle state
   */
  onToggleChange(checked: boolean): void {
    this.isEnabled = checked;
    if (checked) {
      // When enabled, check all checkboxes
      this.control.setValidators(vipCheckboxesValidator(1));
      this.control.updateValueAndValidity();
    } else {
      this.control.clearValidators();
      // When disabled, uncheck all and clear the form control
      this.control.setValue([]);
    }
  }

  /**
   * Initializes form controls for both Series S and F checkboxes
   * @private
   */
  private initializeFormControls(): void {
    const vipLevels = this.control.value;

    this.availablePrinterSeries?.forEach((family) => {
      this.familiesForms[family.family] = this.fb.group({});

      this.availableBusinessLevels[family.family]?.forEach(
        (businessLevel: any) => {
          this.familiesForms[family.family].addControl(
            businessLevel.id.toString(),
            this.fb.control(vipLevels?.includes(businessLevel.id))
          );
        }
      );
    });

    // Subscribe to form value changes
    this.subscribeToFormChanges();
  }

  /**
   * Sets up subscriptions to form changes for both series
   * @private
   */
  private subscribeToFormChanges(): void {
    Object.entries(this.familiesForms).forEach(([key, _value]) => {
      this.familiesForms[key].valueChanges.subscribe(() => {
        this.updateFormControl();
      });
    });
  }

  /**
   * Updates the main form control with the currently selected VIP levels
   * @private
   */
  private updateFormControl(): void {
    if (!this.isEnabled) {
      return;
    }

    const selectedLevels: number[] = [];

    Object.entries(this.familiesForms).forEach(([key]) => {
      Object.entries(this.familiesForms[key].value).forEach(
        ([entryKey, entryValue]) => {
          if (entryValue === true) {
            selectedLevels.push(parseInt(entryKey));
          }
        }
      );
    });

    this.control.setValue(selectedLevels.length > 0 ? selectedLevels : []);
  }

  /**
   * Return only the VIP business levels
   * @param {string} familyName
   * @return {any[]} the list of VIP business levels
   */
  getSeriesVIPBusinessLevels(familyName: string): any[] {
    const businessLevels = this.availableBusinessLevels[familyName].slice();
    businessLevels.splice(0, 1);
    return businessLevels;
  }
}
